// Maps to backend/routers/group.py
import { useState, useCallback } from 'react';
import { env } from '../utils/env';

export interface Member {
  id: string;
  user_id?: string | null;
  email: string;
  display_name: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'pending' | 'ghost';
  is_ghost?: boolean;
  joined_at?: string;
  avatar_url?: string;
  notification_preferences?: {
    is_temporary_ghost?: boolean;
    invited_email?: string;
    [key: string]: any;
  };
}

export const useGroupMembers = (groupId: string | number, accessToken: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApiError = async (response: Response, defaultMessage: string) => {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `${defaultMessage}: ${response.status}`);
  };

  const inviteMember = useCallback(async (email: string, role: 'admin' | 'member' = 'member') => {
    if (!accessToken || !groupId) {
      throw new Error('Authentication required');
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, role }),
      });

      if (!response.ok) {
        await handleApiError(response, 'Failed to invite member');
      }

      return await response.json();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to invite member';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [accessToken, groupId]);

  const removeMember = useCallback(async (memberId: string, isGhost: boolean = false) => {
    if (!accessToken || !groupId) {
      throw new Error('Authentication required');
    }

    try {
      setLoading(true);
      setError(null);

      // Use different endpoints based on whether the member is a ghost
      const endpoint = isGhost
        ? `${env.API_URL}/api/groups/${groupId}/members/ghost/${memberId}`
        : `${env.API_URL}/api/groups/${groupId}/members/${memberId}`;

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        await handleApiError(response, 'Failed to remove member');
      }

      return await response.json();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove member';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [accessToken, groupId]);

  const updateMemberRole = useCallback(async (memberId: string, role: 'admin' | 'member') => {
    if (!accessToken || !groupId) {
      throw new Error('Authentication required');
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/members/${memberId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ role }),
      });

      if (!response.ok) {
        await handleApiError(response, 'Failed to update member role');
      }

      return await response.json();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update member role';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [accessToken, groupId]);

  const remindToJoin = useCallback(async (email: string) => {
    if (!accessToken || !groupId) {
      throw new Error('Authentication required');
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/members/remind`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        await handleApiError(response, 'Failed to send reminder');
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send reminder';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [accessToken, groupId]);

  const addGhostMember = useCallback(async (displayName: string, email?: string) => {
    if (!accessToken || !groupId) {
      throw new Error('Authentication required');
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${env.API_URL}/api/groups/${groupId}/members/ghost`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ display_name: displayName, email }),
      });

      if (!response.ok) {
        await handleApiError(response, 'Failed to add ghost member');
      }

      const data = await response.json();

      // Ensure the returned member is properly marked as a ghost in the UI
      return {
        ...data,
        is_ghost: true, // Explicitly set is_ghost for frontend usage
        status: data.status || 'ghost' // Ensure status is set to ghost
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add ghost member';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [accessToken, groupId]);

  return {
    loading,
    error,
    inviteMember,
    removeMember,
    updateMemberRole,
    remindToJoin,
    addGhostMember,
  };
}; 